/**
 * @bos/portal3 — the run WORKBENCH's pure model: the client-side view of one run, folded
 * from the journal (`runs.get`) and the live SSE frames, with zero DOM and zero I/O so
 * every law here is unit-testable.
 *
 * THE REDUCER LAW: `applyFrame` is pure and IDEMPOTENT — a frame whose `seq` is not
 * strictly greater than what the view has already folded returns the SAME state object,
 * unchanged. The SSE attach replays exactly what was missed, but a StrictMode double
 * mount, a reconnect overlap, or a stop-echo must never double a thread line. The view is
 * a FUNCTION OF THE JOURNAL: rebuilding it from `runs.get` after a refresh produces the
 * same screen, because nothing here is state the journal doesn't carry.
 *
 * STATUS HONESTY (mirrors of server law, cited not imported — a value import of
 * @bos/portal3-server would drag the server runtime into the bundle):
 *   - the terminal set and the outcome→status mapping mirror `runs/store.ts` /
 *     `runs/driver.ts` (`awaiting-owner` is LIVE, never terminal — the machine is waiting
 *     for a human, and this screen must read that way);
 *   - first terminal wins: once the view reaches a terminal status, only a `started`
 *     frame (the resume's opening act — the mirror of the store's explicit `reopen`)
 *     moves it back to 'running'.
 *
 * WHAT IS DELIBERATELY NOT HERE: no percentages, no per-stage durations, no cost — the
 * server produces none of those for this screen (batch 4 brief; `cost` frames exist in
 * the vocabulary but are unrendered this batch, by the brief's explicit scope). Elapsed
 * time and the stamp of the last observed frame are the only waiting signals.
 */
import type { Run, RunEventRecord, RunStatus, RunWithJournal } from '@bos/portal3-server';

// ── status law (mirrors runs/store.ts) ───────────────────────────────────────

/** The finished states. 'awaiting-owner' is deliberately NOT here — it is live. */
export const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'completed',
  'stopped',
  'failed',
  'aborted',
  'orphaned',
  'budget-stopped',
]);

export function isTerminalStatus(status: RunStatus | null): boolean {
  return status !== null && TERMINAL_STATUSES.has(status);
}

/** The statuses the resume door accepts (mirror of the executor's policy: everything
 *  unfinished except a live run; 'completed' is the one refusal-on-principle). */
export function isResumableStatus(status: RunStatus | null): boolean {
  return (
    status === 'stopped' ||
    status === 'failed' ||
    status === 'aborted' ||
    status === 'orphaned' ||
    status === 'budget-stopped'
  );
}

// ── the view ─────────────────────────────────────────────────────────────────

export interface RunView {
  /** The run row (row truth from `runs.get`; `run.status` is superseded by `status` below). */
  readonly run: Run | null;
  /** The highest journal seq folded in — the SSE attach cursor. NEVER reattach from 0. */
  readonly lastSeq: number;
  /** Every folded frame, in seq order — the thread renders straight off this. */
  readonly records: readonly RunEventRecord[];
  /** The status as the journal tells it (seeded from the row, moved by terminal frames). */
  readonly status: RunStatus | null;
}

export const emptyRunView: RunView = { run: null, lastSeq: 0, records: [], status: null };

/** A frame's plain shape, or null when the `ev` is not a phase-bearing object. */
export function asFrame(ev: unknown): { phase: string; [k: string]: unknown } | null {
  if (typeof ev !== 'object' || ev === null || Array.isArray(ev)) return null;
  const phase = (ev as { phase?: unknown }).phase;
  return typeof phase === 'string' ? (ev as { phase: string }) : null;
}

const KNOWN_STATUSES: ReadonlySet<string> = new Set<RunStatus>([
  'running',
  'awaiting-owner',
  'completed',
  'stopped',
  'failed',
  'aborted',
  'orphaned',
  'budget-stopped',
]);

/** Mirror of the server's `runStatusForOutcome` (runs/driver.ts), tolerant of unknowns. */
function statusForOutcome(outcomeStatus: unknown): RunStatus | null {
  switch (outcomeStatus) {
    case 'ok':
    case 'dry-lower-only':
      return 'completed';
    case 'stalled':
      return 'awaiting-owner';
    case 'aborted':
      return 'aborted';
    case 'budget':
      // The machine stopped AT THE CEILING it was given — obedience, never a failure.
      return 'budget-stopped';
    case 'config-invalid':
    case 'dry-lower-failed':
    case 'graph-failed':
    case 'verdict-not-ok':
      return 'failed';
    default:
      return null; // an unknown outcome moves nothing — never invent a status
  }
}

/** What status the view holds after this frame (first terminal wins; `started` is the
 *  one door back to 'running' — the resume's opening frame, mirror of `reopen`). */
function statusAfterFrame(current: RunStatus | null, ev: unknown): RunStatus | null {
  const frame = asFrame(ev);
  if (frame === null) return current;
  if (frame.phase === 'started') return 'running';
  if (isTerminalStatus(current)) return current; // first terminal wins
  switch (frame.phase) {
    case 'done': {
      const outcome = frame.outcome as { status?: unknown } | undefined;
      return statusForOutcome(outcome?.status) ?? current;
    }
    case 'stopped':
      return 'stopped';
    case 'error':
      return 'failed';
    case 'reconciled': {
      const to = frame.to;
      return typeof to === 'string' && KNOWN_STATUSES.has(to) ? (to as RunStatus) : current;
    }
    default:
      return current;
  }
}

/**
 * Fold ONE journaled record into the view. Pure and idempotent: a record with
 * `seq <= lastSeq` (a duplicate, a replay overlap, a stale delivery) returns the view
 * UNCHANGED — the same object, so React sees no update at all.
 */
export function applyFrame(view: RunView, record: RunEventRecord): RunView {
  if (record.seq <= view.lastSeq) return view;
  return {
    run: view.run,
    lastSeq: record.seq,
    records: [...view.records, record],
    status: statusAfterFrame(view.status, record.ev),
  };
}

/**
 * Seed the view from `runs.get`: fold the journal, then let the ROW's status win — at
 * get-time the row is at least as fresh as the journal it was read with. Frames arriving
 * after the seed (over SSE) move status through `applyFrame` as usual.
 */
export function seedRunView(payload: Pick<RunWithJournal, 'run' | 'events'>): RunView {
  let view: RunView = { run: payload.run, lastSeq: 0, records: [], status: payload.run.status };
  for (const record of payload.events) view = applyFrame(view, record);
  return { ...view, status: payload.run.status };
}

/** Merge a fresher run row (a stop/resume mutation's return) into the view. */
export function withRun(view: RunView, run: Run): RunView {
  return { ...view, run, status: run.status };
}

/**
 * WHICH stop is this? The stall kind of the latest arc, as the journal tells it (the
 * driver's `done` frame carries `outcome.stall.kind` — 'er-approval' for the data-model
 * halt, 'signoff'/'chain-gate' for a question round, mirrors of newpip's app-driver).
 * A `started` frame opens a new arc and clears it. Null when the journal has not said —
 * the page then falls back on what the server's read surfaces report from disk, and
 * invents nothing.
 */
export function stallKindOf(view: RunView): string | null {
  let kind: string | null = null;
  for (const record of view.records) {
    const frame = asFrame(record.ev);
    if (frame === null) continue;
    if (frame.phase === 'started') kind = null;
    if (frame.phase === 'done') {
      const outcome = frame.outcome as { stall?: { kind?: unknown } } | undefined;
      kind = typeof outcome?.stall?.kind === 'string' ? outcome.stall.kind : null;
    }
  }
  return kind;
}

/** The stalled STAGE of the latest arc (the `done` frame's `outcome.stall.stage`) — which
 *  chain stage a 'stage-approval' stop is waiting at. Null when the journal has not said. */
export function stallStageOf(view: RunView): string | null {
  let stage: string | null = null;
  for (const record of view.records) {
    const frame = asFrame(record.ev);
    if (frame === null) continue;
    if (frame.phase === 'started') stage = null;
    if (frame.phase === 'done') {
      const outcome = frame.outcome as { stall?: { stage?: unknown } } | undefined;
      stage = typeof outcome?.stall?.stage === 'string' ? outcome.stall.stage : null;
    }
  }
  return stage;
}

/** The stamp of the last observed frame (the "last progress" truth), else the start. */
export function lastProgressIso(view: RunView): string | null {
  const last = view.records[view.records.length - 1];
  if (last !== undefined) return last.at;
  return view.run?.startedAt ?? null;
}

// ── the stage rail ───────────────────────────────────────────────────────────

/** Chain stage keys exactly as the factory emits them, with the OWNER's words for each
 *  (§9.11: nobody outside the repo should see 'er', 'rbac', 'statemachine'…). */
const CHAIN_STAGES: ReadonlyArray<readonly [string, string]> = [
  ['er', 'Data model'],
  ['statemachine', 'Lifecycles'],
  ['process', 'Operations'],
  ['rules', 'Rules'],
  ['rbac', 'Permissions'],
  ['audit', 'Audit'],
  ['decomposition', 'Modules'],
];

/** The chain-stage keys whose review is OPENABLE from the rail: the data model (its
 *  review is batch 5/6's section) plus the six batch-7 read surfaces. Exactly the
 *  CHAIN_STAGES keys. */
export type ReviewStageKey =
  | 'er'
  | 'statemachine'
  | 'process'
  | 'rules'
  | 'rbac'
  | 'audit'
  | 'decomposition';

/** The six later review stages (everything openable except the data model). Since the
 *  A3/A4 batch these are no longer read-only: each carries a signature-bound approval
 *  door and a prose disagree door (StageReview.tsx). */
export type StageReviewKey = Exclude<ReviewStageKey, 'er'>;

/** The owner-sentence subject for each later stage ("the lifecycles ARE settled…", "the
 *  audit plan IS settled…"). MIRROR of the chain's owner phrase table (`@bos/newpip`
 *  stage-approval.ts) — cited, never imported (no server runtime in the bundle). */
export const STAGE_SUBJECTS: Readonly<Record<StageReviewKey, { readonly subject: string; readonly plural: boolean }>> = {
  statemachine: { subject: 'lifecycles', plural: true },
  process: { subject: 'operations', plural: true },
  rules: { subject: 'rules', plural: true },
  rbac: { subject: 'permissions', plural: true },
  audit: { subject: 'audit plan', plural: false },
  decomposition: { subject: 'module split', plural: false },
};

export function isReviewStageKey(key: string): key is ReviewStageKey {
  return CHAIN_STAGES.some(([k]) => k === key);
}

/** The owner's label for a chain stage key (the rail's own words). */
export function reviewStageLabel(key: ReviewStageKey): string {
  return CHAIN_LABELS.get(key) ?? key;
}

/** Every rail row that opens a review section (batch 8): the chain stages plus the
 *  CHECKS row — the end of the run, the verdict screen. The build rows still open
 *  nothing (no read surface exists for them yet, and none is faked). */
export type OpenableStageKey = ReviewStageKey | 'checks';

export function isOpenableStageKey(key: string): key is OpenableStageKey {
  return key === 'checks' || isReviewStageKey(key);
}

/** The owner's label for any openable rail row. THE VERDICT is the name the download card
 *  has always sent people looking for (prior review M2) — "Checks" undersold it. */
export function openableStageLabel(key: OpenableStageKey): string {
  return key === 'checks' ? 'The verdict' : reviewStageLabel(key);
}

/** The rail rows after the chain: sign-off (where the run waits for you), then the build
 *  half (dry-lower / factory / screens / compile), then certification + verdict. */
const RAIL: ReadonlyArray<readonly [string, string]> = [
  ...CHAIN_STAGES,
  ['signoff', 'Sign-off'],
  ['build', 'Code'],
  ['checks', 'The verdict'],
];

export type RailState = 'done' | 'current' | 'waiting' | 'pending';

export interface RailStage {
  readonly key: string;
  readonly label: string;
  readonly state: RailState;
  /** The latest honest one-liner for this row (real counts only, never invented). */
  readonly note?: string;
}

function railIndexForFrame(frame: { phase: string; [k: string]: unknown }): number {
  switch (frame.phase) {
    case 'stage': {
      const i = CHAIN_STAGES.findIndex(([key]) => key === frame.stage);
      return i; // -1 for an unknown stage name — moves nothing
    }
    case 'er-review':
      return 0; // the ER-approval halt sits at the data model
    case 'stage-review': {
      // The later-stage approval halt sits at its boundary's first pending stage.
      const i = CHAIN_STAGES.findIndex(([key]) => key === frame.stage);
      return i;
    }
    case 'signoff':
      return RAIL.findIndex(([key]) => key === 'signoff');
    case 'dry-lower':
    case 'factory-module':
    case 'screens':
    case 'compile':
      return RAIL.findIndex(([key]) => key === 'build');
    case 'certify':
    case 'verdict':
      return RAIL.findIndex(([key]) => key === 'checks');
    default:
      return -1;
  }
}

/** The honest per-row note a frame contributes (counts the server really sent). */
function railNoteForFrame(frame: { phase: string; [k: string]: unknown }): string | undefined {
  switch (frame.phase) {
    case 'stage': {
      const ownerNote = frame.ownerNote;
      const note = frame.note;
      if (typeof ownerNote === 'string' && ownerNote !== '') return ownerNote;
      return typeof note === 'string' ? note : undefined;
    }
    case 'er-review':
      return typeof frame.entityCount === 'number' ? `${frame.entityCount} entities` : undefined;
    case 'stage-review': {
      const n = Array.isArray(frame.stages) ? frame.stages.length : 0;
      return n > 1 ? `${n} reviews waiting for you` : 'waiting for your approval';
    }
    case 'signoff':
      if (frame.status === 'awaiting') {
        const n = typeof frame.residualCount === 'number' ? frame.residualCount : undefined;
        return n === undefined ? 'questions for you' : `${n} question${n === 1 ? '' : 's'} for you`;
      }
      return frame.status === 'waived' ? 'approved by you' : 'cleared itself';
    case 'dry-lower':
      switch (frame.status) {
        case 'start':
          return typeof frame.total === 'number' ? `pre-checking ${frame.total} modules` : 'pre-checking modules';
        case 'module':
          return typeof frame.slug === 'string' ? `pre-checked ${frame.slug}` : undefined;
        case 'passed':
          return 'pre-build check passed';
        case 'failed':
          return 'pre-build check failed';
        default:
          return undefined;
      }
    case 'factory-module': {
      const slug = typeof frame.slug === 'string' ? frame.slug : 'module';
      const n = typeof frame.n === 'number' ? frame.n : undefined;
      const m = typeof frame.m === 'number' ? frame.m : undefined;
      const count = n !== undefined && m !== undefined ? ` (${n} of ${m} done)` : '';
      if (frame.status === 'start') return `writing ${slug}${count}`;
      if (frame.status === 'reused') return `${slug} reused${count}`;
      return `${slug} written${count}`;
    }
    case 'screens':
      if (frame.ran === true && typeof frame.count === 'number') return `${frame.count} screens`;
      return frame.ran === true ? 'screens generated' : 'screens skipped';
    case 'compile':
      return typeof frame.verdict === 'string' ? `compile: ${frame.verdict}` : undefined;
    case 'certify':
      if (frame.passed === true) return 'certification passed';
      if (frame.passed === false) return 'certification failed';
      return 'certification ran';
    case 'verdict':
      return frame.ok === true ? 'verdict: OK' : 'verdict: not OK';
    default:
      return undefined;
  }
}

/** Derive the whole rail from the view — a pure projection of the folded frames. */
export function deriveRail(view: RunView): RailStage[] {
  let reached = -1;
  const notes = new Map<string, string>();
  for (const record of view.records) {
    const frame = asFrame(record.ev);
    if (frame === null) continue;
    const idx = railIndexForFrame(frame);
    if (idx === -1) continue;
    if (idx > reached) reached = idx;
    const note = railNoteForFrame(frame);
    if (note !== undefined) notes.set(RAIL[idx][0], note);
  }

  return RAIL.map(([key, label], i) => {
    let state: RailState;
    if (i < reached) state = 'done';
    else if (i === reached) {
      if (view.status === 'completed') state = 'done';
      else if (view.status === 'awaiting-owner') state = 'waiting';
      else state = 'current';
    } else {
      // Nothing observed yet: while the run is live the first row is where the machine is.
      state = reached === -1 && i === 0 && view.status === 'running' ? 'current' : 'pending';
    }
    const note = notes.get(key);
    return { key, label, state, ...(note !== undefined ? { note } : {}) };
  });
}

/** The rail row the machine is at right now (for the artifact pane's honest header). */
export function currentRailStage(rail: readonly RailStage[]): RailStage | null {
  return rail.find((s) => s.state === 'current' || s.state === 'waiting') ?? null;
}

// ── the thread ───────────────────────────────────────────────────────────────

export type ThreadTone = 'muted' | 'info' | 'ok' | 'warn' | 'danger';

/**
 * WHICH LAYER an item belongs to (prior review B1, the client half).
 *
 *   'log'  — a raw `message` frame, printed by the machine for the machine: absolute
 *            paths, tool output, vendor errors. It is the forensic record and it is TRUE,
 *            so it is never deleted — the Journal collapses each stretch of it behind one
 *            quiet toggle instead, and no other room shows it at all.
 *   'card' — a line written for the owner. These are the story.
 */
export type ThreadLayer = 'log' | 'card';

export interface ThreadItem {
  readonly seq: number;
  readonly at: string;
  readonly tone: ThreadTone;
  readonly kind: ThreadLayer;
  /** A short heading line; absent for plain log lines. */
  readonly title?: string;
  readonly body?: string;
  /**
   * THE ROOM THIS LINE OPENS, when it names one — a room slug, or `''` for the Overview.
   * The Journal is the record, not the workbench, so a row that reports something the owner
   * can still act on hands them the way there instead of leaving them to hunt for it. Only
   * the rows that genuinely lead somewhere carry it; the record does not grow decoration.
   */
  readonly room?: string;
}

const CHAIN_LABELS = new Map(CHAIN_STAGES);

/**
 * Project ONE journaled record to a thread item — or null for frames the thread does not
 * show (`cost` is deliberately unrendered this batch: the brief scopes cost display out,
 * so the number is neither drawn nor invented elsewhere).
 */
export function threadItemFor(record: RunEventRecord): ThreadItem | null {
  const frame = asFrame(record.ev);
  if (frame === null) return null;
  const base = { seq: record.seq, at: record.at, kind: 'card' as ThreadLayer };

  switch (frame.phase) {
    case 'message':
      return typeof frame.message === 'string'
        ? { ...base, kind: 'log', tone: 'muted', body: frame.message }
        : null;
    case 'started':
      return {
        ...base,
        tone: 'info',
        title: record.seq <= 1 ? 'Run started' : 'Run picked back up',
        // The frame keeps the FACTORY's field name; what it carries is the name this build
        // is made under — the APP's, or (for a run older than apps) the business's.
        body: typeof frame.projectName === 'string' ? `Building for ${frame.projectName}.` : undefined,
      };
    case 'stage': {
      const label =
        typeof frame.stage === 'string' ? (CHAIN_LABELS.get(frame.stage) ?? frame.stage) : 'Stage';
      const note = railNoteForFrame(frame);
      return { ...base, tone: 'info', title: label, body: note };
    }
    case 'er-review':
      return {
        ...base,
        tone: 'warn',
        title: 'Data model ready for review',
        body: typeof frame.entityCount === 'number' ? `${frame.entityCount} entities derived.` : undefined,
      };
    case 'stage-review': {
      const label = typeof frame.stage === 'string' ? (CHAIN_LABELS.get(frame.stage) ?? frame.stage) : 'A stage';
      const names = Array.isArray(frame.stages)
        ? frame.stages
            .map((s) => {
              const key = (s as { stage?: unknown })?.stage;
              return typeof key === 'string' ? (CHAIN_LABELS.get(key) ?? key) : null;
            })
            .filter((n): n is string => n !== null)
        : [];
      return {
        ...base,
        tone: 'warn',
        title: names.length > 1 ? `Ready for review: ${names.join(', ')}` : `${label} ready for review`,
        body: 'Read and approve to continue — nothing more is spent while it waits.',
      };
    }
    case 'signoff':
      if (frame.status === 'awaiting') {
        const n = typeof frame.residualCount === 'number' ? frame.residualCount : null;
        const a = typeof frame.advisoryCount === 'number' ? frame.advisoryCount : null;
        const parts: string[] = [];
        if (n !== null) parts.push(`${n} question${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} you`);
        if (a !== null && a > 0) parts.push(`${a} assumption${a === 1 ? '' : 's'} recorded for you`);
        // ONE LINE PER ROUND, and it leads somewhere: the front desk carries the round's
        // summary and says which room each question is waiting in. The Journal never hosts
        // the cards themselves — it is the record of what happened, not the place to answer.
        return {
          ...base,
          tone: 'warn',
          title: 'The machine stopped to ask you something',
          room: '',
          ...(parts.length > 0 ? { body: parts.join(' · ') } : {}),
        };
      }
      return {
        ...base,
        tone: 'ok',
        title: frame.status === 'waived' ? 'Sign-off cleared with your approvals' : 'Sign-off cleared itself',
      };
    case 'dry-lower':
    case 'factory-module':
    case 'screens':
    case 'compile':
    case 'certify':
      return { ...base, tone: 'info', body: railNoteForFrame(frame) ?? frame.phase };
    case 'verdict': {
      const reasons = Array.isArray(frame.reasons) ? frame.reasons.filter((r) => typeof r === 'string') : [];
      return frame.ok === true
        ? { ...base, tone: 'ok', title: 'Final verdict: OK' }
        : { ...base, tone: 'danger', title: 'Final verdict: not OK', body: reasons.join('; ') || undefined };
    }
    case 'done': {
      const outcome = (frame.outcome ?? {}) as {
        status?: unknown;
        stall?: { kind?: unknown; questionCount?: unknown };
      };
      switch (outcome.status) {
        case 'ok':
        case 'dry-lower-only':
          return { ...base, tone: 'ok', title: 'Build finished' };
        case 'stalled': {
          // The DATA-MODEL halt is its own door, not a question round — and its
          // `questionCount` carries the ENTITY count (app-driver.ts law), so rendering
          // it as "N questions" would be a lie. Name the stop it actually is.
          if (outcome.stall?.kind === 'er-approval') {
            return {
              ...base,
              tone: 'warn',
              title: 'Waiting for you',
              body: 'Your data model is ready — read it and approve it to continue. Nothing more is spent while it waits.',
            };
          }
          // The LATER-STAGE review halt (A3): also a review door, not a question round —
          // and its `questionCount` carries the pending-approval count, not questions.
          if (outcome.stall?.kind === 'stage-approval') {
            const stageKey = (outcome.stall as { stage?: unknown }).stage;
            const label = typeof stageKey === 'string' ? (CHAIN_LABELS.get(stageKey) ?? null) : null;
            return {
              ...base,
              tone: 'warn',
              title: 'Waiting for you',
              body:
                label !== null
                  ? `The ${label} review is ready for you — read and approve to continue. Nothing more is spent while it waits.`
                  : 'A stage review is ready for you — read and approve to continue. Nothing more is spent while it waits.',
            };
          }
          const q = outcome.stall?.questionCount;
          return {
            ...base,
            tone: 'warn',
            title: 'Waiting for you',
            body:
              typeof q === 'number'
                ? `The machine stopped to ask — ${q} question${q === 1 ? '' : 's'}. It continues once you answer.`
                : 'The machine stopped to ask you something. It continues once you answer.',
          };
        }
        case 'aborted':
          return { ...base, tone: 'danger', title: 'Cut off mid-step', body: 'The run ended without a result.' };
        case 'budget': {
          // The machine stopped AT the spending limit it was given — a deliberate,
          // honest stop, so it reads as a wait for a decision (warn), never as breakage
          // (danger). Every number below is the tripping check's own (the `done` frame's
          // outcome.budget); when the journal carries none, the words carry none.
          const budget = (frame.outcome as { budget?: { stage?: unknown; usedCredits?: unknown; capCredits?: unknown } } | undefined)
            ?.budget;
          const stageKey = typeof budget?.stage === 'string' ? budget.stage : null;
          const where =
            stageKey === 'factory'
              ? 'while writing the code modules'
              : stageKey !== null
                ? `at the ${(CHAIN_LABELS.get(stageKey) ?? stageKey).toLowerCase()} stage`
                : null;
          const used = typeof budget?.usedCredits === 'number' ? budget.usedCredits : null;
          const cap = typeof budget?.capCredits === 'number' ? budget.capCredits : null;
          const spent =
            used !== null && cap !== null
              ? `This stretch used ${used.toLocaleString('en-US')} credits against a limit of ${cap.toLocaleString('en-US')}.`
              : null;
          const body = [
            where !== null ? `It reached the limit ${where}.` : null,
            spent,
            'Everything built so far is saved and is not paid for again — resuming continues from here, and the machine may spend up to the limit again before its next stop.',
          ]
            .filter((s): s is string => s !== null)
            .join(' ');
          return { ...base, tone: 'warn', title: 'Stopped at the spending limit', body };
        }
        case 'dry-lower-failed':
          return { ...base, tone: 'danger', title: 'Stopped at the pre-build check' };
        case 'graph-failed':
          return { ...base, tone: 'danger', title: 'Stopped: the module graph was refused' };
        case 'verdict-not-ok':
          return { ...base, tone: 'danger', title: 'Finished, but the final verdict is not OK' };
        case 'config-invalid':
          return { ...base, tone: 'danger', title: 'Stopped: invalid configuration' };
        default:
          return { ...base, tone: 'info', title: 'Run ended' };
      }
    }
    case 'error':
      return {
        ...base,
        tone: 'danger',
        title: 'The run hit an error',
        body: typeof frame.error === 'string' ? frame.error : undefined,
      };
    case 'stopped':
      return { ...base, tone: 'info', title: 'You stopped this run', body: 'Everything already checkpointed is kept. You can resume it.' };
    case 'reconciled':
      return {
        ...base,
        tone: 'warn',
        title: 'The server restarted while this run was going',
        body:
          typeof frame.to === 'string' && typeof frame.because === 'string'
            ? `Recorded as ${frame.to} — ${frame.because}`
            : undefined,
      };
    case 'medic': {
      // THE AUTOMATIC REPAIR THREAD (medic batch). Every line mirrors a fact the server's medic
      // router really established — and a repair is NEVER worded as success unless the frame says a
      // deterministic re-check actually cleared ('repaired', 'resuming verified', 'ended certified').
      switch (frame.status) {
        case 'dispatched':
          return {
            ...base,
            tone: 'warn',
            title: 'Automatic repair started',
            body: 'The build failed a check the machine can classify, so it is sending in its repair mechanic. Nothing a repair does is accepted unless the checks pass again afterwards.',
          };
        case 'attempt': {
          const n = typeof frame.attempt === 'number' ? frame.attempt : null;
          const m = typeof frame.maxAttempts === 'number' ? frame.maxAttempts : null;
          return {
            ...base,
            tone: 'info',
            body: n !== null && m !== null ? `Repair attempt ${n} of ${m} is running.` : 'A repair attempt is running.',
          };
        }
        case 'attempt-outcome':
          switch (frame.outcome) {
            case 'repaired':
              return { ...base, tone: 'ok', body: 'The repair passed its re-check.' };
            case 'quarantined':
              return {
                ...base,
                tone: 'warn',
                title: 'A repair needs human review',
                body: 'The proposed fix touched the checking machinery itself, so it was set aside for a person to review and the change was rolled back. The build will not continue on an unreviewed fix.',
              };
            case 'rejected':
              return {
                ...base,
                tone: 'info',
                body: typeof frame.detail === 'string' ? `The repair was rejected by a safety check (${frame.detail}).` : 'The repair was rejected by a safety check.',
              };
            case 'no-fix':
              return { ...base, tone: 'info', body: 'The mechanic found no safe fix and honestly said so.' };
            case 'stopped':
              return { ...base, tone: 'info', body: 'Your stop cut the repair attempt short. Nothing it did was accepted.' };
            default:
              return { ...base, tone: 'info', body: 'The repair did not clear the check.' };
          }
        case 'rebuild': {
          const pkgs = Array.isArray(frame.packages) ? frame.packages.filter((p) => typeof p === 'string') : [];
          return {
            ...base,
            tone: 'info',
            body: pkgs.length > 0 ? `Rebuilding the factory tooling the fix touched (${pkgs.join(', ')}) before continuing.` : 'Rebuilding the factory tooling the fix touched before continuing.',
          };
        }
        case 'resuming':
          return frame.verified === true
            ? { ...base, tone: 'ok', body: 'A verified fix was adopted — the build is picking back up.' }
            : { ...base, tone: 'info', body: 'A factory fix passed its safety checks — the build is re-running to prove it on the real work.' };
        case 'skipped':
          return {
            ...base,
            tone: 'warn',
            title: 'Repair not attempted',
            body: 'Another run is using the repair mechanic right now, so this build stopped instead of repairing. Resuming later will try again.',
          };
        case 'ended':
          if (frame.outcome === 'certified') {
            return { ...base, tone: 'ok', title: 'Automatic repair succeeded', body: 'The repair held — the re-run cleared every check.' };
          }
          if (frame.outcome === 'judge-review') {
            return {
              ...base,
              tone: 'warn',
              title: 'Automatic repair stopped for human review',
              body: 'The proposed fix touched the checking machinery itself. It was set aside for a person; the build stopped rather than continue on an unreviewed fix.',
            };
          }
          if (frame.outcome === 'owner' || frame.outcome === 'pack-finding' || frame.outcome === 'fill-triage') {
            return {
              ...base,
              tone: 'warn',
              title: 'The machine hands this one to a person',
              body: typeof frame.reason === 'string' ? frame.reason : undefined,
            };
          }
          return {
            ...base,
            tone: 'warn',
            title: 'Automatic repair gave up',
            body: typeof frame.reason === 'string' ? frame.reason : undefined,
          };
        default:
          return { ...base, tone: 'muted', body: 'medic' };
      }
    }
    case 'cost':
      return null; // deliberately unrendered this batch (see module header)
    default:
      // Tomorrow's phases: name them honestly, invent nothing about them.
      return { ...base, tone: 'muted', body: frame.phase };
  }
}

// ── wording ──────────────────────────────────────────────────────────────────

/** Short badge label per status. 'awaiting-owner' must read as WAITING FOR A HUMAN. */
export function statusLabel(status: RunStatus | null): string {
  switch (status) {
    case 'running':
      return 'Building';
    case 'awaiting-owner':
      return 'Waiting for you';
    case 'completed':
      return 'Finished';
    case 'stopped':
      return 'Stopped';
    case 'failed':
      return 'Failed';
    case 'aborted':
      return 'Cut off';
    case 'orphaned':
      return 'Interrupted';
    case 'budget-stopped':
      return 'At its spending limit';
    default:
      return 'Loading';
  }
}

/** The one-sentence explanation under the header, per status. `stallKind` (when the
 *  journal named one) picks the honest sentence for WHICH stop this is: the data-model
 *  review door and the question round are different waits and must read differently. */
export function statusSentence(status: RunStatus | null, stallKind?: string | null): string {
  switch (status) {
    case 'running':
      return 'The machine is building. You can close this page — the run keeps going, and this page rebuilds itself when you come back.';
    case 'awaiting-owner':
      if (stallKind === 'er-approval') {
        return 'The machine has stopped on purpose: your data model is ready for you to review. Nothing more is spent until you approve it.';
      }
      if (stallKind === 'stage-approval') {
        return 'The machine has stopped on purpose: a stage of the plan is ready for you to review. Nothing more is spent until you approve it.';
      }
      return 'The machine has stopped to ask you something. Nothing is lost while it waits — it continues as soon as you answer.';
    case 'completed':
      return 'This run finished.';
    case 'stopped':
      return 'You stopped this run. Everything already checkpointed is kept — resume to pick up where it left off.';
    case 'failed':
      return 'This run failed. Resuming replays what already passed for free and retries the failing step.';
    case 'aborted':
      return 'This run was cut off mid-step without a result. You can resume it.';
    case 'orphaned':
      return 'The server lost track of this run after a restart. You can resume it.';
    case 'budget-stopped':
      return 'The machine stopped at this account’s spending limit — deliberately, before spending more; nothing broke. Everything built so far is saved and is never paid for again. Resume to continue from here; the limit itself is set by an administrator.';
    default:
      return '';
  }
}

/** "Aug 24, 08:07 PM" — one instant in the reader's own locale. */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "3m 20s" / "1h 04m" — elapsed between two instants, honest to the second. */
export function formatElapsed(fromIso: string, nowMs: number): string {
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return '';
  const s = Math.max(0, Math.floor((nowMs - from) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

// ── the rooms (the run stops being one long page) ────────────────────────────
//
// A run is a building. The shell is the hallway — breadcrumb, name, status, the nav —
// and each room is a real URL that survives a refresh because it rebuilds from its own
// read surface (the per-room lazy fetches already worked this way; they were just all
// pointed at one screen). The rail's two natural halves become the two nav groups: THE
// PLAN (what your app will be) and THE BUILD (what it became). Sign-off is not a room:
// its questions belong on the front desk, where the person is already standing.

export type RoomGroup = 'plan' | 'build';

export interface RoomDef {
  /** The path segment under `/app/runs/:id/` — the room's addressable name. */
  readonly slug: string;
  readonly label: string;
  readonly group: RoomGroup;
  /** Which rail row's state and note describe this room (several rooms may share one). */
  readonly railKey: string;
  /** The chain-stage read this room renders, when it renders one. */
  readonly stage?: ReviewStageKey;
}

/** The rooms of the run, in nav order. `journal` and the Overview are not here — they are
 *  the hallway's own two doors, listed separately because they are not part of the work. */
export const ROOMS: readonly RoomDef[] = [
  { slug: 'plan/data-model', label: 'Data model', group: 'plan', railKey: 'er', stage: 'er' },
  { slug: 'plan/lifecycles', label: 'Lifecycles', group: 'plan', railKey: 'statemachine', stage: 'statemachine' },
  { slug: 'plan/operations', label: 'Operations', group: 'plan', railKey: 'process', stage: 'process' },
  { slug: 'plan/rules', label: 'Rules', group: 'plan', railKey: 'rules', stage: 'rules' },
  { slug: 'plan/permissions', label: 'Permissions', group: 'plan', railKey: 'rbac', stage: 'rbac' },
  { slug: 'plan/audit', label: 'Audit', group: 'plan', railKey: 'audit', stage: 'audit' },
  { slug: 'plan/modules', label: 'Modules', group: 'plan', railKey: 'decomposition', stage: 'decomposition' },
  { slug: 'build/code', label: 'Code', group: 'build', railKey: 'build' },
  { slug: 'build/screens', label: 'Screens', group: 'build', railKey: 'build' },
  { slug: 'verdict', label: 'The verdict', group: 'build', railKey: 'checks' },
];

export const ROOM_GROUP_LABEL: Readonly<Record<RoomGroup, string>> = {
  plan: 'The plan',
  build: 'The build',
};

/** The room that holds one chain stage's review, or null when the key names no room. */
export function roomForStage(stage: string): RoomDef | null {
  return ROOMS.find((r) => r.stage === stage) ?? null;
}

export function roomBySlug(slug: string): RoomDef | null {
  return ROOMS.find((r) => r.slug === slug) ?? null;
}

// ── the one thing that needs you (the front desk's first section) ────────────

/**
 * WHAT, IF ANYTHING, IS BEING WAITED ON — derived from the journal alone (status +
 * `done.outcome.stall`), so every room can render the same answer without another fetch.
 *
 *   'stage-door'  — a review is on offer at a named stage: the room holds the door.
 *   'questions'   — the machine stopped to ask; the round renders on the Overview.
 *   'unnamed'     — the run is waiting and the journal did not say at what. Nothing is
 *                   invented; the Overview's own honest resume path handles it.
 */
export type OwnerCall =
  | { readonly kind: 'stage-door'; readonly stage: ReviewStageKey; readonly label: string; readonly room: string }
  | { readonly kind: 'questions' }
  | { readonly kind: 'unnamed' };

/** What the machine is waiting for, or null when it is not waiting for the owner at all. */
export function ownerCallOf(view: RunView): OwnerCall | null {
  if (view.status !== 'awaiting-owner') return null;
  const kind = stallKindOf(view);
  if (kind === 'er-approval') {
    return { kind: 'stage-door', stage: 'er', label: reviewStageLabel('er'), room: 'plan/data-model' };
  }
  if (kind === 'stage-approval') {
    const stage = stallStageOf(view);
    if (stage !== null && isReviewStageKey(stage)) {
      const room = roomForStage(stage);
      if (room !== null) {
        return { kind: 'stage-door', stage, label: reviewStageLabel(stage), room: room.slug };
      }
    }
    return { kind: 'unnamed' };
  }
  if (kind === null) return { kind: 'unnamed' };
  return { kind: 'questions' };
}

/** The sticky banner's sentence: what the machine is waiting for, in the owner's words. */
export function waitingBannerSentence(call: OwnerCall): string {
  switch (call.kind) {
    case 'stage-door':
      return call.stage === 'er'
        ? 'your data model is ready to read and approve'
        : `the ${call.label} review is ready to read and approve`;
    case 'questions':
      return 'it has something to ask you';
    case 'unnamed':
    default:
      return 'it stopped, and this build has not said at what';
  }
}

/**
 * WHAT THE MACHINE IS DOING RIGHT NOW, from the last frame that reported real work — the
 * "nothing needs you" half of the front desk. Every clause below is a fact the frame
 * carried; a phase that reported nothing gets the plain sentence, never a guess.
 */
export function workingSentence(view: RunView): string {
  for (let i = view.records.length - 1; i >= 0; i--) {
    const frame = asFrame(view.records[i].ev);
    if (frame === null) continue;
    switch (frame.phase) {
      case 'factory-module': {
        const slug = typeof frame.slug === 'string' ? frame.slug : null;
        const n = typeof frame.n === 'number' ? frame.n : null;
        const m = typeof frame.m === 'number' ? frame.m : null;
        const count = n !== null && m !== null ? ` (${n} of ${m} done)` : '';
        return slug !== null
          ? `the machine is writing the ${slug} part of your app${count}`
          : `the machine is writing your app’s parts${count}`;
      }
      case 'dry-lower':
        return 'the machine is pre-checking the parts before it writes them';
      case 'screens':
        return 'the machine is making your app’s screens';
      case 'compile':
        return 'the machine is putting your app together';
      case 'certify':
        return 'the machine is checking what it built';
      case 'verdict':
        return 'the machine is composing its final verdict';
      case 'signoff':
        return 'the machine is at the sign-off';
      case 'stage': {
        const key = typeof frame.stage === 'string' ? frame.stage : null;
        const label = key !== null ? CHAIN_LABELS.get(key) : undefined;
        return label !== undefined
          ? `the machine is working on the ${label.toLowerCase()}`
          : 'the machine is working on your plan';
      }
      case 'er-review':
      case 'stage-review':
      case 'started':
        return 'the machine is working';
      default:
        continue;
    }
  }
  return 'the machine is working';
}

// ── the milestones (the Overview's timeline) ─────────────────────────────────

export interface Milestone {
  /** The rail key, or 'started' / 'ended' for the two bookends. */
  readonly key: string;
  readonly label: string;
  /** When the machine FIRST got here. Null only when the journal carried no stamp. */
  readonly at: string | null;
  readonly state: RailState | null;
  /** The row's latest honest one-liner (the rail's own note). */
  readonly note?: string;
  /** Extra facts the journal really carried: re-derivations, an approval wait. */
  readonly detail?: string;
  /** The room this milestone opens, when it has one. */
  readonly room?: string;
  readonly tone: ThreadTone;
}

/** A stage frame that reports a RE-DERIVATION — the chain writes this note exactly once
 *  per real re-derivation (a replayed-from-checkpoint stage says so instead), so counting
 *  them is counting events, not thread lines. */
function isRederiveNote(note: string | undefined): boolean {
  return note !== undefined && /re-?derived/i.test(note);
}

/**
 * A note that reports NO WORK: this stage was replayed from its checkpoint because its
 * inputs did not move. It is true, and it is the single most repeated sentence in a long
 * build's journal — on one measured run it was the standing subtitle under six of ten rail
 * rows at once, which turns a status line into wallpaper. Displays that show one line per
 * stage skip it and let the ✓ carry the meaning; the Journal still has every one of them.
 */
export function isReplayNote(note: string | undefined): boolean {
  return note !== undefined && /resumed from checkpoint/i.test(note);
}

/**
 * THE MILESTONES: one row per stage the run actually reached, bookended by its start and
 * its end. This is the clean story — the Journal keeps the full record.
 *
 * It is a fold over the frames and NOTHING ELSE. A resume replays finished stages from
 * checkpoints and re-emits their frames; that replay moves no milestone (the stamp is the
 * FIRST time the machine got there) and mints no row, which is exactly why the 50 "picked
 * back up" arcs on a long build never appear here.
 */
export function deriveMilestones(view: RunView): Milestone[] {
  const firstAt = new Map<string, string>();
  const notes = new Map<string, string>();
  const rederives = new Map<string, number>();
  const waited = new Set<string>();
  let reached = -1;
  let ending: { title: string; tone: ThreadTone; at: string } | null = null;

  for (const record of view.records) {
    const frame = asFrame(record.ev);
    if (frame === null) continue;

    // The two owner-facing halts, named by the frames that publish them.
    if (frame.phase === 'er-review' && frame.status === 'awaiting') waited.add('er');
    if (frame.phase === 'stage-review' && frame.status === 'awaiting' && typeof frame.stage === 'string') {
      waited.add(frame.stage);
    }
    if (frame.phase === 'signoff' && frame.status === 'awaiting') waited.add('signoff');
    if (frame.phase === 'stage' && typeof frame.stage === 'string' && isRederiveNote(railNoteForFrame(frame))) {
      rederives.set(frame.stage, (rederives.get(frame.stage) ?? 0) + 1);
    }

    const idx = railIndexForFrame(frame);
    if (idx !== -1) {
      const key = RAIL[idx][0];
      if (!firstAt.has(key)) firstAt.set(key, record.at);
      if (idx > reached) reached = idx;
      const note = railNoteForFrame(frame);
      // "replayed free" is not a milestone — it is the absence of one.
      if (note !== undefined && !isReplayNote(note)) notes.set(key, note);
    }

    // How it ended — the LAST terminal frame wins (an earlier arc's ending is superseded).
    const item = threadItemFor(record);
    if (
      item?.title !== undefined &&
      (frame.phase === 'done' || frame.phase === 'stopped' || frame.phase === 'error')
    ) {
      ending = { title: item.title, tone: item.tone, at: record.at };
    }
  }

  const stages: Milestone[] = [];
  RAIL.forEach(([key, label], i) => {
    const at = firstAt.get(key);
    if (at === undefined) return; // never reached — a milestone that did not happen
    const state: RailState =
      i < reached ? 'done' : view.status === 'completed' ? 'done' : view.status === 'awaiting-owner' ? 'waiting' : 'current';
    const room = ROOMS.find((r) => r.railKey === key);
    const bits: string[] = [];
    const n = rederives.get(key) ?? 0;
    if (n > 0) {
      const times = n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`;
      bits.push(`derived again ${times}, after you asked`);
    }
    if (waited.has(key)) bits.push(key === 'signoff' ? 'it stopped to ask you' : 'it stopped for your approval');
    const note = notes.get(key);
    stages.push({
      key,
      label,
      at,
      state,
      tone: state === 'waiting' ? 'warn' : 'info',
      ...(note !== undefined ? { note } : {}),
      ...(bits.length > 0 ? { detail: bits.join(' · ') } : {}),
      ...(room !== undefined ? { room: room.slug } : {}),
    });
  });

  // A TIMELINE THAT SHOWS ITS CLOCKS MUST OBEY THEM. The chain's own order is not always
  // the order a build lived: a run that reached the sign-off, then went back and
  // re-derived three stages, really did touch sign-off first. Sorting by when the machine
  // FIRST got somewhere keeps the stamps and the order telling the same story; stages that
  // share a stamp (one boundary publishing several) keep the chain's order between them.
  stages.sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));

  const out: Milestone[] = [];
  if (view.run !== null) {
    out.push({
      key: 'started',
      label: 'You asked for this build',
      at: view.run.startedAt,
      state: null,
      tone: 'info',
    });
  }
  out.push(...stages);
  if (ending !== null && isTerminalStatus(view.status)) {
    out.push({ key: 'ended', label: ending.title, at: ending.at, state: null, tone: ending.tone });
  }
  return out;
}

// ── the journal's two layers ─────────────────────────────────────────────────

export interface ThreadGroup {
  readonly kind: ThreadLayer;
  readonly items: readonly ThreadItem[];
}

/**
 * Split the thread into alternating stretches of owner cards and raw machine log
 * (prior review B1). The log is kept — every line of it — and folded: on a long build
 * it outnumbers the cards more than four to one, and a person reading their own build
 * should meet the story first and the machinery only when they ask for it.
 */
export function groupThreadItems(items: readonly ThreadItem[]): ThreadGroup[] {
  const groups: ThreadGroup[] = [];
  let current: { kind: ThreadLayer; items: ThreadItem[] } | null = null;
  for (const item of items) {
    if (current === null || current.kind !== item.kind) {
      current = { kind: item.kind, items: [item] };
      groups.push(current);
    } else {
      current.items.push(item);
    }
  }
  return groups;
}

// ── the browser tab (prior review B3/p2) ─────────────────────────────────────

/**
 * The tab's title. A halted run must be readable from another tab — that is the whole
 * point — so WAITING wins over whichever room is open; everything else names the room so
 * history and bookmarks stay useful.
 */
export function runDocumentTitle(
  buildName: string,
  roomLabel: string | null,
  status: RunStatus | null,
): string {
  const name = buildName.trim() === '' ? 'Build' : buildName;
  if (status === 'awaiting-owner') return `⏸ Waiting for you — ${name} · Sorvel`;
  if (roomLabel !== null) return `${roomLabel} — ${name} · Sorvel`;
  return `${name} · Sorvel`;
}
